import { Trash2, ChevronDown, Check, Search } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { RestaurantHoverCard } from "./RestaurantHoverCard";

interface Restaurant {
  id: string;
  name: string;
  neighborhoodName?: string;
  isActive?: boolean;
}

interface TableModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  onClose: () => void;
  onSubmit: (data: {
    restaurantId: string;
    capacity: number;
    reservationName: string;
  }) => void;
  onDelete?: () => void;
  restaurants: Restaurant[];
  initialData?: {
    restaurantId: string;
    capacity: number;
    reservationName: string;
    hasUsers: boolean;
  };
  isLoading?: boolean;
  isDeleting?: boolean;
}

export function TableModal({
  isOpen,
  mode,
  onClose,
  onSubmit,
  onDelete,
  restaurants,
  initialData,
  isLoading = false,
  isDeleting = false,
}: TableModalProps) {
  const [restaurantId, setRestaurantId] = useState("");
  const [capacity, setCapacity] = useState(4);
  const [reservationName, setReservationName] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && mode === "edit" && initialData) {
      setRestaurantId(initialData.restaurantId);
      setCapacity(initialData.capacity);
      setReservationName(initialData.reservationName);
    } else if (isOpen && mode === "add") {
      setRestaurantId("");
      setCapacity(4);
      setReservationName("");
    }
    setDropdownOpen(false);
    setSearchQuery("");
  }, [isOpen, mode, initialData]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [dropdownOpen]);

  const selectedRestaurant = restaurants.find((r) => r.id === restaurantId);
  const filteredRestaurants = searchQuery
    ? restaurants.filter((r) =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.neighborhoodName?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : restaurants;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ restaurantId, capacity, reservationName });
  };

  const handleClose = () => {
    if (!isLoading && !isDeleting) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal modal-open" onClick={handleClose}>
      <div
        className="modal-box max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-xl">
            {mode === "add" ? "Add New Table" : "Edit Table"}
          </h3>
          {mode === "edit" && onDelete && (
            <button
              onClick={onDelete}
              disabled={isLoading || isDeleting || initialData?.hasUsers}
              className="btn btn-ghost btn-sm text-error"
              title={initialData?.hasUsers ? "Cannot delete table with assigned users" : "Delete table"}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Restaurant *</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => !isLoading && setDropdownOpen(!dropdownOpen)}
                className="select select-bordered w-full text-left flex items-center justify-between"
                disabled={isLoading}
              >
                <span className={selectedRestaurant ? "" : "text-base-content/40"}>
                  {selectedRestaurant
                    ? `${selectedRestaurant.name}${selectedRestaurant.neighborhoodName ? ` - ${selectedRestaurant.neighborhoodName}` : ""}`
                    : "Select a restaurant"}
                </span>
                <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {dropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-base-100 border border-base-300 rounded-lg shadow-xl overflow-hidden">
                  <div className="p-2 border-b border-base-300">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-base-content/40" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search restaurants..."
                        className="input input-sm input-bordered w-full pl-8"
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredRestaurants.length === 0 ? (
                      <div className="px-3 py-4 text-sm text-base-content/50 text-center">No restaurants found</div>
                    ) : (
                      filteredRestaurants.map((restaurant) => (
                        <RestaurantHoverCard key={restaurant.id} restaurantId={restaurant.id} placement="right-start" tapToOpen={false}>
                          <button
                            type="button"
                            onClick={() => {
                              setRestaurantId(restaurant.id);
                              setDropdownOpen(false);
                              setSearchQuery("");
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-base-200 flex items-center justify-between gap-2 transition-colors ${
                              restaurantId === restaurant.id ? "bg-primary/10 text-primary" : ""
                            }`}
                          >
                            <span className="truncate">
                              {restaurant.name}
                              {restaurant.neighborhoodName && (
                                <span className="text-base-content/50 ml-1">- {restaurant.neighborhoodName}</span>
                              )}
                            </span>
                            {restaurantId === restaurant.id && <Check className="w-4 h-4 shrink-0" />}
                          </button>
                        </RestaurantHoverCard>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">
                {mode === "add" ? "Table Name (optional)" : "Reservation Name"}
              </span>
            </label>
            <input
              type="text"
              value={reservationName}
              onChange={(e) => setReservationName(e.target.value)}
              placeholder="e.g., Table 1, Main Room, etc."
              className="input input-bordered w-full"
              disabled={isLoading}
            />
          </div>

          <div className="form-control">
            <label className="label">
              <span className="label-text font-medium">Capacity *</span>
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={capacity}
              onChange={(e) => setCapacity(parseInt(e.target.value) || 1)}
              className="input input-bordered w-full"
              disabled={isLoading}
              required
            />
          </div>

          <div className="modal-action">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading || isDeleting}
              className="btn btn-ghost"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || isDeleting || !restaurantId}
              className="btn btn-primary"
            >
              {isLoading ? (
                <>
                  <span className="loading loading-spinner loading-sm"></span>
                  {mode === "add" ? "Creating..." : "Saving..."}
                </>
              ) : mode === "add" ? (
                "Create Table"
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

